import PaymentCategories from '../../enums/PaymentCategories';
import Page from '../page/page';
import PaymentSectionAttributeReader from '../payment-methods/paymentSectionAttributeReader';
import {
  getDeliveryOptionsTrackingAttributes,
  getNewSelectedDeliveryOptionRequestData,
} from '../selectors/DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import getAvailableArvatoOptionsIfSelected
from '../selectors/PaymentOptionSelectors/Arvato/getAvailableArvatoOptionsIfSelected';
import getPaymentOptionsFromOrderSummary from '../selectors/PaymentOptionSelectors/getPaymentOptionsFromOrderSummary';
import attributes from '../tracking/attributes';
import bigQueryMetricRecorder from '../tracking/bigQueryMetricRecorder';
import Track from '../utils/tracking';
import {extractOrderSummaryDataAndLog} from '../utils/orderSummaryMetrics';
import * as types from './action-types';
import {
  updateDeliveryCountrySelectionOnAPI,
  updateDeliveryOptionSelectionOnAPI,
  updatePaymentOptionSelectionOnAPI,
} from './APIActions/CheckoutAPIActions';
import {handleAppliedGiftcards} from './handleAppliedGiftcards';
import handleNoAppliedGiftcards from './handleNoAppliedGiftcards';
import {getSettings} from '../selectors/selectors';
import validateBasketItemChanges from './validateBasketItemChanges';

export const OrderSummaryUpdateReasons = {
  SELECTED_DELIVERY_OPTION: 'SELECTED_DELIVERY_OPTION',
  UPDATED_DELIVERY_COUNTRY: 'UPDATED_DELIVERY_COUNTRY',
  UPDATED_PAYMENT_TYPE: 'UPDATED_PAYMENT_TYPE',
  UPDATED_APPLIED_CREDIT: 'UPDATED_APPLIED_CREDIT',
  APPLYING_GIFTCARD: 'APPLYING_GIFTCARD',
  REMOVING_GIFTCARD: 'REMOVING_GIFTCARD',
  EDITING_BASKET: 'EDITING_BASKET',
};

export const updateOrderSummary = orderSummary => (dispatch, getState) => {
  const settings = getSettings(getState());

  if (orderSummary.giftCardSummaries?.length) {
    dispatch(handleAppliedGiftcards(orderSummary));
  } else if (!orderSummary.giftCardSummaries) {
    dispatch(handleNoAppliedGiftcards());
  }
  
  const orderSummaryPaymentOptions = getPaymentOptionsFromOrderSummary(getState());
  
  let orderSummaryObject = orderSummary;
  
  if (!orderSummary.paymentOptions) {
    orderSummaryObject = {
      ...orderSummary,
      paymentOptions: orderSummaryPaymentOptions,
    };
  }

  if (settings?.enable_editable_basket) {
    validateBasketItemChanges(getState(), dispatch, orderSummary);
    
    extractOrderSummaryDataAndLog(
      orderSummaryObject,
      'orderSummary_updateOrderSummary',
    );
  }
  
  dispatch({type: types.UPDATE_ORDER_SUMMARY, orderSummary: orderSummaryObject});
};

export function setOrderSummaryUpdating(reason) {
  return {type: types.SET_ORDER_SUMMARY_UPDATING, reason};
}

export function errorUpdatingOrderSummary() {
  return {type: types.SET_ORDER_SUMMARY_ERROR};
}

export function updateAccountCreditMessage(message, messageType) {
  return {type: types.SET_ACCOUNT_CREDIT_MESSAGE, message, messageType};
}

export const getOrderSummaryForDeliveryOption = () => (dispatch, getState) => new Promise((resolve) => {
  const deliveryOptionRequestData = getNewSelectedDeliveryOptionRequestData(getState());
    
  if (!deliveryOptionRequestData.deliveryOptionsRequest) {
    Track.track('Customer tried to select Delivery Option without valid Delivery Address inputs.');
    resolve();
  } else if (deliveryOptionRequestData.groupSelections.length > 0) {
    dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.SELECTED_DELIVERY_OPTION));
      
    try {
      bigQueryMetricRecorder('selectedDeliveryOptions', getDeliveryOptionsTrackingAttributes(getState()));
    } catch (error) {
      Track.track(error);
    }
    
    return dispatch(updateDeliveryOptionSelectionOnAPI(deliveryOptionRequestData))
      .then((response) => {
        if (response && response.status === 200 && response.msg) {
          dispatch(updateOrderSummary(response.msg));
          dispatch(getAvailableArvatoOptionsIfSelected());
        } else {
          Track.error(`Error updating Delivery Option Selection on API: ${JSON.stringify(response)}`);
          dispatch(errorUpdatingOrderSummary());
        }
      })
      .catch((error) => {
        Track.error(`Caught error sending Delivery Option Selection to API: ${error}`);
        dispatch(errorUpdatingOrderSummary());
      })
      .finally(resolve);
  } else {
    Track.track('Customer tried to select Delivery Option without valid Delivery groups.');
    resolve('No update needed as no groups selected');
  }
});

export const getOrderSummaryForCountry = data => (dispatch) => {
  dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.UPDATED_DELIVERY_COUNTRY));
  
  return dispatch(updateDeliveryCountrySelectionOnAPI(data))
    .then((response) => {
      if (response && response.status === 200 && response.msg) {
        dispatch(updateOrderSummary(response.msg));
      } else {
        Track.error(`Error updating Delivery Country Selection on API: ${JSON.stringify(response)}`);
        dispatch(errorUpdatingOrderSummary());
      }
    })
    .catch((error) => {
      Track.error(`Caught error sending Delivery Country Selection to API: ${error}`);
      dispatch(errorUpdatingOrderSummary());
    });
};

export function getOrderSummaryForPaymentOption(paymentOptionName, paymentOptionIndex, changeCause, paymentMethodCategoryString = PaymentCategories.NONE.description) {
  return (dispatch) => {
    try {
      const eventType = (changeCause === 'click') ? 'click' : 'impression';
      bigQueryMetricRecorder('selectPaymentMethod',
        [
          ...PaymentSectionAttributeReader.getPaymentMethodAttribute(paymentOptionName, paymentMethodCategoryString),
          attributes.getAttribute('paymentSelectionReason', changeCause),
        ],
        [],
        eventType);
    } catch (error) {
      Track.track(error);
    }
    
    if (!Page.isOnPage('checkout')) return null;
    
    dispatch(setOrderSummaryUpdating(OrderSummaryUpdateReasons.UPDATED_PAYMENT_TYPE));
    
    return dispatch(updatePaymentOptionSelectionOnAPI({
      name: paymentOptionName.startsWith('ADYEN_V2') ? 'adyen_v2' : paymentOptionName,
      index: paymentOptionIndex,
      category: paymentMethodCategoryString,
    }))
      .then((response) => {
        if (response && response.status === 200 && response.msg) {
          dispatch(updateOrderSummary(response.msg));
        } else {
          Track.error(`Error updating Payment Option Selection on API: ${JSON.stringify(response)}`);
          dispatch(errorUpdatingOrderSummary());
        }
      })
      .catch((error) => {
        Track.error(`Caught error sending Payment Option Selection to API: ${error}`);
        dispatch(errorUpdatingOrderSummary());
      });
  };
}
